import { segue } from "global-action-creators"
import {
    resourceGetDetail,
    resourceGetList,
    resourcePatchDetail,
    resourcePostList,
} from "shared/djangio/action-creators"
import emailTemplateActionTypes from "outbox/templates/action-types"
import * as emailTemplateSelectors from "outbox/templates/selectors"
import { templateSelector, selectVariantFieldsForTemplate } from "shared/outbox/selectors"
import blankBeeFree from "app/static/outbox-templates/blankBeeFree"
import { generateStaticUrl } from "shared/imports/sharedHelperFunctions"
import {
    updatePlaceholders,
    createBulkEmail,
    segueToBulkEmail,
    duplicateAttachmentsAndPatch,
} from "outbox/action-creators"
import swalConfigs from "app/static/frontend/swalConfigs"
import swal from "sweetalert"
import BACKENDERROR from "app/static/frontend/imports/backenderror"

/**
 ********************
 * Template Helpers *
 ********************/

/**
 * Function that loads a beefree default template
 *
 * @name getBeeDefault
 * @function
 * @param {String} slug - string of the template
 * @param {Function} callback - function that is executed on load
 */
const getBeeDefault = (slug, callback) => $.getJSON(generateStaticUrl(`outbox-templates/${slug}.json`), callback)

// method that duplicates attachments
const duplicateAttachments = (attachments) =>
    DjangIO.app.userdata.models.Attachment.objects.filter({ id__in: attachments }).action("duplicate_attachments").GET()

/**
 **********************
 * Navigation actions *
 **********************/

/* Action creator for going to the main page */
export const segueToManageTemplates = () => segue("/outbox/manage_templates/")

/* Action Creator for going to the create page */
export const segueToCreateTemplate = () => segue("/outbox/create_template/")

/**
 * Action creator for going to the template page
 *
 * @param {Number} templateId the dbid of the template
 */
export const segueToEmailTemplate = (templateId) => segue(`/outbox/create_template/${templateId}`)

/**
 * Action Creator that resets the state
 */
export const resetEmailTemplate = () => ({ type: emailTemplateActionTypes.RESET_EMAIL_TEMPLATE })

/**
 * Action creator that updates the email template
 *
 * @name updateEmailTemplate
 * @function
 * @param {Object} payload - object to be merged into state
 */
export const updateEmailTemplate = (payload) => ({
    type: emailTemplateActionTypes.UPDATE_EMAIL_TEMPLATE,
    payload,
})

/**
 ****************************
 * Template API Actions *
 ****************************/

/**
 * Action creator for loading a single emailTemplate
 *
 * @param {String} id the database id of the email template
 * @returns {Promise} returns the request promise
 */
export const getEmailTemplate = (id) => (dispatch) => {
    // get all of the placeholders that a user has access to for templates
    dispatch(updatePlaceholders(null, { is_template: true }))
    return dispatch(
        resourceGetDetail(
            DjangIO.app.userdata.outbox.models.email_template.EmailTemplate.objects.full_dehydrate("attachments"),
            {
                start: emailTemplateActionTypes.GET_EMAIL_TEMPLATE_START,
                fail: emailTemplateActionTypes.GET_EMAIL_TEMPLATE_FAIL,
                success: emailTemplateActionTypes.GET_EMAIL_TEMPLATE_SUCCESS,
            },
            id,
        ),
    )
}

/**
 * Action Creator that gets the templates - we want to get them all
 * (they aren't big and no one would have more then 1000
 *
 */
export const getAllTemplates =
    (override = false) =>
    (dispatch, getState) => {
        // be smart about your gets!
        const currentTemplates = emailTemplateSelectors.getImmutableTemplates(getState())
        const isLoadingTemplates = emailTemplateSelectors.getTemplateStatus(getState())
        if (override !== true && (isLoadingTemplates || currentTemplates.size > 0)) {
            // if override isn't true and we are loading templates, or
            // we have templates, reject
            return false
        }
        // dispatch at end
        return dispatch(
            resourceGetList(DjangIO.app.userdata.outbox.models.email_template.EmailTemplate.objects.all(), {
                start: emailTemplateActionTypes.GET_ALL_EMAIL_TEMPLATES_START,
                fail: emailTemplateActionTypes.GET_ALL_EMAIL_TEMPLATES_FAIL,
                success: emailTemplateActionTypes.GET_ALL_EMAIL_TEMPLATES_SUCCESS,
            }),
        )
    }

/**
 * Action creator for updating a emailTemplate
 *
 * @param {Number} id=null dbId of the template
 * @param {Object} kwargs={} update args (if overwriting store)
 * @param {Object} payload={} extra args to pass
 * @param {String} updateAction='' action to run on patch
 * @returns {Promise} promise from patch
 */
export const patchEmailTemplate =
    (id = null, kwargs = {}, payload = {}) =>
    (dispatch, getState) => {
        const state = getState()
        return dispatch(
            resourcePatchDetail(
                DjangIO.app.userdata.outbox.models.email_template.EmailTemplate,
                {
                    start: emailTemplateActionTypes.PATCH_EMAIL_TEMPLATE_START,
                    fail: emailTemplateActionTypes.PATCH_EMAIL_TEMPLATE_FAIL,
                    success: emailTemplateActionTypes.PATCH_EMAIL_TEMPLATE_SUCCESS,
                },
                id || emailTemplateSelectors.getId(state),
                { ...emailTemplateSelectors.patchTemplateSelector(state), ...kwargs },
                payload,
            ),
        )
    }

/**
 * autosaveTemplate
 *
 * - saves the template if there is an id, if it is ok
 *   it isn't currently sending, saving, or previewing
 *
 * @name autosaveTemplate
 * @function
 */
export const autosaveTemplate = () => (dispatch, getState) => {
    const state = getState()

    // get the time of last form change and last autosave
    const lastSave = emailTemplateSelectors.getLastAutosavedMoment(state)
    const lastChange = emailTemplateSelectors.getLastUpdatedMoment(state)

    // do not autosave if there have been no changes since this last autosave
    if (lastChange >= lastSave) {
        dispatch({ type: emailTemplateActionTypes.AUTOSAVE_EMAIL_TEMPLATE_START })
        // if the email is updating or if there isn't an email - don't save
        if (emailTemplateSelectors.getId(state) === 0 || emailTemplateSelectors.isUpdating(state)) {
            dispatch({ type: emailTemplateActionTypes.AUTOSAVE_EMAIL_TEMPLATE_BLOCKED })
            return
        }
        // otherwise - patch the bulk email
        dispatch(patchEmailTemplate()).then((response) => {
            dispatch({ type: emailTemplateActionTypes.AUTOSAVE_EMAIL_TEMPLATE_SUCCESS })
            return response
        })
    }
}

/**
 * Method that actually creates the template
 *
 * @name postTemplate
 * @function
 * @param {Object} kwargs={} - rgs to overwrite the default / add
 * @param {Object} payload={} - payload
 */
export const postTemplate = (kwargs = {}, payload = {}) =>
    resourcePostList(
        DjangIO.app.userdata.outbox.models.email_template.EmailTemplate,
        {
            start: emailTemplateActionTypes.POST_EMAIL_TEMPLATE_START,
            fail: emailTemplateActionTypes.POST_EMAIL_TEMPLATE_FAIL,
            success: emailTemplateActionTypes.POST_EMAIL_TEMPLATE_SUCCESS,
        },
        {
            creator: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
            last_editor: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
            name: `Untitled ${new Date().toLocaleString()}`,
            organization: DjangIO.app.userdata.models.Organization.foreignKey(Userdata.organization_id),
            ...kwargs,
        },
        payload,
    )

/**
 * Action Creator that deletes templates
 *
 * @name deleteTemplate
 * @function
 * @param {Number} id - Number of the template to delete
 * @param {Object} kwargs={} - rgs to overwrite the default / add
 * @param {Object} payload={} - payload
 */
export const deleteTemplate = (id, kwargs = {}, payload = {}) =>
    resourcePatchDetail(
        DjangIO.app.userdata.outbox.models.email_template.EmailTemplate,
        {
            start: emailTemplateActionTypes.DELETE_EMAIL_TEMPLATE_START,
            fail: emailTemplateActionTypes.DELETE_EMAIL_TEMPLATE_FAIL,
            success: emailTemplateActionTypes.DELETE_EMAIL_TEMPLATE_SUCCESS,
        },
        id,
        kwargs,
        payload,
    )

/**
 * method that is called on close
 *
 * @name saveAndCloseTemplate
 * @function
 */
export const saveAndCloseTemplate = () => (dispatch) => {
    dispatch(patchEmailTemplate(null, true)).then((response) => {
        dispatch(resetEmailTemplate())
        dispatch(getAllTemplates())
        return response
    })
    dispatch(segueToManageTemplates())
}

/**
 ***************
 * ATTACHMENTS *
 ***************/

/**
 * Action creator for adding attachments
 *
 * @param {Object} newA new attachment object
 */
export const addTemplateAttachment = (newAttachments) => (dispatch, getState) => {
    const attachments = emailTemplateSelectors.getAttachments(getState())
    // make sure it isn't a duplicate
    const dedupedAttachments = newAttachments.filter(
        (newAttachment) => !attachments.some((attachment) => attachment.id === newAttachment.id),
    )
    if (dedupedAttachments.length) {
        dispatch(
            updateEmailTemplate({
                attachments: attachments.concat(newAttachments),
            }),
        )
    }
}

/**
 * Action creator for deleting attachments
 *
 * @param {Object} event the event object
 * @param {Number} attachmentId the attachment
 */
export const deleteTemplateAttachment = (event, attachmentId) => {
    event.preventDefault()
    event.stopPropagation()
    return (dispatch, getState) =>
        $.ajax({
            url: `/outbox/delete/${attachmentId}/`,
            type: "POST",
            cache: false,
            processData: false,
            contentType: "application/json",
            data: JSON.stringify({}),
            success: () =>
                dispatch(
                    updateEmailTemplate({
                        attachments: emailTemplateSelectors
                            .getAttachments(getState())
                            .filter((attachment) => attachment.id !== attachmentId),
                    }),
                ),
            error(jqXHR, textStatus) {
                // eslint-disable-next-line no-console
                return console.error(`ERRORS: ${textStatus}`)
            },
        })
}

/**
 ****************************
 * Template Action Creators *
 ****************************/
/**
 * method called on template creation
 *
 * @name createTemplate
 * @function
 *
 * @param {String} templateType - One of [summernote, beefree, or a beefree default]
 * @returns {Promise} promise
 */
export const createTemplate = (templateType) => (dispatch) => {
    // make sure it is reset
    dispatch(resetEmailTemplate())
    // helper function on creation that
    const onSuccess = (response) => {
        dispatch(updatePlaceholders(null, { is_template: true }))
        dispatch(segueToEmailTemplate(response.id))
    }
    // dispatch correct actions
    if (templateType === "summernote" || templateType === "beefree") {
        // if it isn't a beefree default we want to dispatch the load
        dispatch(
            postTemplate({
                html_content: "",
                compose_tool: templateType,
                json_content: templateType === "beefree" ? blankBeeFree : {},
                limited_to_teams: Userdata.default_limited_to_teams,
            }),
        ).then(onSuccess)
    } else {
        getBeeDefault(templateType, (templateJson) =>
            dispatch(
                postTemplate({
                    html_content: "",
                    compose_tool: "beefree",
                    json_content: templateJson,
                    limited_to_teams: Userdata.default_limited_to_teams,
                }),
            ).then(onSuccess),
        )
    }
}

// Below stuff is like copied... not ideal...

/**
 * Helper function that updates the beefree instance when a template is loaded
 *
 * @name loadHelper
 * @function
 * @param {Object} newJson - the BeeFree json
 * @param {Object} state - the state of the store
 */
const loadHelper = (newJson, state) => {
    if (emailTemplateSelectors.getComposeTool(state) === "beefree") {
        const beeFreeInstance = emailTemplateSelectors.getBeePlugin(state)
        if (beeFreeInstance && beeFreeInstance.load) {
            beeFreeInstance.load(newJson)
        }
    }
}

/**
 * Action creator that takes in a template object and loads it into the store
 *
 * @name loadTemplate
 * @function
 * @param {Object} template - object containing all of the template attributes
 */
export const loadEmailTemplate = (template) => (dispatch, getState) => {
    /**
     * Helper function that loads the template
     *
     * @name onLoad
     * @function
     * @param {Object} newJson - the json object
     * @param {String} newHtml - the html string
     */
    const onLoad = (newJson, newHtml) => {
        // if we don't have a default template then we can just
        // update the email
        dispatch(
            updateEmailTemplate({
                json_content: newJson || {},
                html_content: newHtml || "",
                attachments: [],
            }),
        )
        loadHelper(newJson, getState())
        // if there are attachments, then we need to duplicate them!
        if (template.attachments && template.attachments.length > 0) {
            duplicateAttachments(
                template.attachments.map(DjangIO.app.userdata.models.Attachment.idFromResourceUri),
            ).then((response) => dispatch(updateEmailTemplate({ attachments: response.attachments })))
        }
    }
    // if the template is the default then we have to get it.
    // otherwise, just call the load function
    if (template.default) {
        getBeeDefault(template.slug, (templateJson) => onLoad(templateJson, template.slug))
    } else {
        onLoad(template.json_content, template.html_content)
    }
}

/**
 * ActionCreator to duplicate an email
 *
 * @name duplicateTemplate
 * @function
 * @param {Object} template - object containing the template content
 * @param {String} name - string name of the template
 */
export const duplicateTemplate = (template, name) => (dispatch) => {
    // make sure to reset
    dispatch(resetEmailTemplate())
    // dispatch the create event
    return dispatch(
        postTemplate({
            html_content: template.html_content,
            json_content: template.json_content,
            compose_tool: template.compose_tool,
            limited_to_teams: template.limited_to_teams,
            attachments: [],
            name,
        }),
    ).then((response) => {
        // alert success
        swal("Success", `Your template "${name}" was saved`, "success")
        dispatch(segueToEmailTemplate(response.id))
        // if there are attachments make sure we duplicate them
        if (template.attachments && template.attachments.length > 0) {
            duplicateAttachments(
                template.attachments.map((a) => DjangIO.app.userdata.models.Attachment.idFromResourceUri(a)),
            ).then((attachmentResponse) => {
                dispatch(updateEmailTemplate({ attachments: attachmentResponse.attachments }))
                dispatch(patchEmailTemplate())
            })
        }
        return response
    })
}

/**
 * Action Creator that makes a template
 *
 * @name saveMessageAsTemplate
 * @function
 * @param {String} name - name of the tmplate
 * @returns {Promise} Returns a promise
 */
export const saveMessageAsTemplate = () => async (dispatch, getState) => {
    const { value: confirm, state } = await qual.fire(swalConfigs.OUTBOX_SAVE_TEMPLATE_ALERT_CONFIG)
    if (confirm) {
        if (!state.name) {
            qual.fire({
                icon: "exclamation-triangle",
                iconFamily: "fas",
                title: "Error",
                text: "Please name your template",
            })
            return
        }

        await dispatch(postTemplate({ name: state.name, ...templateSelector(getState()) }, { skipMerge: true }))

        qual.fire({
            icon: "check-circle",
            iconFamily: "fas",
            title: "Success",
            text: "Your template was created",
        })
    }
}

/**
 * Action Creator that makes a template from a provided BulkEmail
 *
 * @name saveMessageAsTemplate
 * @function
 * @param {String} name - name of the tmplate
 * @returns {Promise} Returns a promise
 */
export const saveVariantAsTemplate = (variantId) => async (dispatch, getState) => {
    const { value: confirm, state } = await qual.fire(swalConfigs.OUTBOX_SAVE_TEMPLATE_ALERT_CONFIG)

    if (confirm) {
        if (!state.name) {
            qual.fire({
                icon: "exclamation-triangle",
                iconFamily: "fas",
                title: "Error",
                text: "Please name your template",
            })
            return
        }

        await dispatch(
            postTemplate(
                {
                    name: state.name,
                    ...selectVariantFieldsForTemplate(variantId)(getState()),
                },
                { skipMerge: true },
            ),
        )

        qual.fire({
            icon: "check-circle",
            iconFamily: "fas",
            title: "Success",
            text: "Your template was created",
        })
    }
}

export const createEmailFromTemplate = (template) => async (dispatch) => {
    const isConfirm = await swal({
        title: `Create an email using "${template.name}" template`,
        text: "Would you like to create an email using this template?",
        icon: "info",
        buttons: [true, true],
    })

    if (isConfirm) {
        swal({
            title: "Creating Email",
            text: "Just a moment...",
            icon: "info",
            buttons: {},
            closeOnClickOutside: false,
        })

        try {
            const response = await dispatch(
                createBulkEmail({
                    compose_tool: template.compose_tool,
                    html_content: template.html_content,
                    json_content: template.json_content,
                    subject: `${template.name}`,
                }),
            )

            if (template.attachments.length) {
                await dispatch(
                    duplicateAttachmentsAndPatch(
                        template.attachments.map((a) => DjangIO.app.userdata.models.Attachment.idFromResourceUri(a)),
                    ),
                )
            }

            dispatch(segueToBulkEmail(response.id))
            swal("Success", "Your email was created", "success")
        } catch (error) {
            await BACKENDERROR(error, {
                title: "Error",
                text: "There was a problem creating the email",
                icon: "error",
            })
        }
    }
}

export const resetRequestError =
    (error = "") =>
    (dispatch) => {
        dispatch({ type: emailTemplateActionTypes.RESET_REQUEST_ERROR, error })
    }
